import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Command, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uiSettings, setUiSettings] = useState({ show_demo_credentials: false, brand_name: "Ads Intelligence CRM", login_tagline: "" });

  useEffect(() => {
    api.get("/public/ui-settings")
      .then((r) => setUiSettings(r.data))
      .catch(() => {});
  }, []);
  // NOTE: we never auto-fill the email/password inputs from a public
  // setting — even when ``show_demo_credentials`` is on, the values are
  // shown as a hint block below the form so the user copy-pastes them
  // intentionally. Auto-filling production logins from a public endpoint
  // is a security smell.

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background" data-testid="login-page">
      <div className="flex flex-col justify-center px-8 md:px-20 py-10">
        <div className="flex items-center gap-2.5 mb-10">
          <div className="w-9 h-9 bg-primary flex items-center justify-center rounded-sm">
            <Command className="w-4.5 h-4.5 text-primary-foreground" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading font-semibold text-lg tracking-tight leading-none">{uiSettings.brand_name}</div>
            <div className="label-mono mt-1">Agency-grade command center</div>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="font-heading text-4xl font-light tracking-tight leading-tight mb-2">
            Welcome back.
          </h1>
          <p className="text-sm text-muted-foreground mb-10">
            Sign in to monitor, analyze, and act on every Google Ads account — from one console.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label-mono block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full h-11 px-3 border border-border bg-card focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm rounded-sm"
                required
              />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full h-11 px-3 border border-border bg-card focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm rounded-sm"
                required
              />
            </div>

            {error && (
              <div className="text-xs text-[hsl(var(--critical))] border border-[hsl(var(--critical))]/20 bg-[hsl(var(--critical))]/10 px-3 py-2 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-primary text-primary-foreground font-medium text-sm rounded-sm hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
            </button>
          </form>

          {uiSettings.show_demo_credentials && (
            <div className="mt-10 pt-6 border-t border-border" data-testid="demo-credentials-block">
              <div className="label-mono mb-2">Demo accounts</div>
              <div className="text-xs text-muted-foreground space-y-1 font-mono">
                <div>admin@adsintel.com / Admin@12345</div>
                <div>manager@adsintel.com / Manager@123</div>
                <div>analyst@adsintel.com / Analyst@123</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="hidden md:block relative border-l border-border">
        <img
          src="https://images.unsplash.com/photo-1664188574934-e36c36bcf09c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWwlMjBnbGFzcyUyMGxpZ2h0aW5nJTIwY2FsbXxlbnwwfHx8fDE3NzY4Mjc5MjJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="font-heading text-2xl font-light text-white tracking-tight leading-tight max-w-md drop-shadow-lg">
            "{uiSettings.login_tagline || "One unified console for every account, every campaign, every decision."}"
          </div>
        </div>
      </div>
    </div>
  );
}
